import React, { useEffect, useMemo, useState } from "react";
import { HiOutlineSearch, HiOutlineX, HiOutlineExternalLink, HiOutlinePlus, HiOutlineBan } from "react-icons/hi";
import { useFloating, autoUpdate, offset, flip, shift, useDismiss, useRole, useInteractions, FloatingPortal } from "@floating-ui/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import API from "../../api";
import { capture } from "../../instrument";
import { EXAM_TYPE, EXAM_TYPE_LABEL } from "../../utils";
import { rankBySearch } from "../../utils/search";

const getTableLabel = (table) => table.codes.map((code) => code.value).join(", ");

const EXAM_TYPE_ORDER = [
  EXAM_TYPE.DEFAULT,
  EXAM_TYPE.SANG,
  EXAM_TYPE.URINE,
  EXAM_TYPE.ECHANTILLON,
  EXAM_TYPE.H24,
  EXAM_TYPE.SEROLOGIE,
  EXAM_TYPE.PCR,
  EXAM_TYPE.SELLE,
  EXAM_TYPE.AUTRE,
];
const URINE_BUCKETS = [EXAM_TYPE.URINE, EXAM_TYPE.ECHANTILLON, EXAM_TYPE.H24];

const getTableTypes = (table) => {
  const buckets = new Set(table.codes.map((code) => code.type));
  if (URINE_BUCKETS.some((bucket) => buckets.has(bucket))) URINE_BUCKETS.forEach((bucket) => buckets.add(bucket));
  const mainBuckets = [...buckets].filter((bucket) => bucket !== EXAM_TYPE.ECHANTILLON && bucket !== EXAM_TYPE.H24);
  if (mainBuckets.length > 1 || table.exams.some((exam) => exam.type === EXAM_TYPE.DEFAULT)) buckets.add(EXAM_TYPE.DEFAULT);
  return EXAM_TYPE_ORDER.filter((type) => buckets.has(type));
};

const findTablesByCodes = (tables, codes) => tables.filter((table) => table.codes.some((code) => codes.includes(code.value)));

const rankTables = (tables, query) => rankBySearch(tables, query, (table) => [...table.codes.map((code) => code.value), ...table.exams.map((exam) => exam.value)].join(" "));

const getMatchingExam = (table, query) => {
  const needle = query.trim().toLowerCase().split(/\s+/)[0] || "";
  return (needle && table.exams.find((exam) => exam.value.toLowerCase().includes(needle))?.value) || table.exams[0]?.value || "";
};

const getDefaultType = (act, table) => {
  const types = getTableTypes(table);
  if (act.codeType !== EXAM_TYPE.DEFAULT && types.includes(act.codeType)) return act.codeType;
  return types[0] === EXAM_TYPE.DEFAULT ? null : types[0];
};

const AddExamPopover = ({ act, referenceElement, onClose, onAdded, onIgnored, source }) => {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState(act.text || "");
  const [highlighted, setHighlighted] = useState(0);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [hasSkippedCodesHint, setHasSkippedCodesHint] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: true,
    onOpenChange: (next) => {
      if (next === false) onClose();
    },
    elements: { reference: referenceElement },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });
  const { getFloatingProps } = useInteractions([dismiss, role]);

  const { data: tables = [], isLoading } = useQuery({
    queryKey: ["tables", { showDeleted: false }],
    queryFn: async () => {
      const response = await API.get("/table");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    staleTime: 60_000,
  });

  const tablesFromCodes = useMemo(() => findTablesByCodes(tables, act.codes), [tables, act.codes]);
  const results = useMemo(() => {
    const ranked = rankTables(tables, query).filter((table) => !tablesFromCodes.includes(table));
    return [...tablesFromCodes, ...ranked].slice(0, 30);
  }, [tables, query, tablesFromCodes]);

  const pickTable = (table) => {
    setSelectedTable(table);
    setSelectedType(getDefaultType(act, table));
  };

  useEffect(() => {
    if (tablesFromCodes.length === 1 && !selectedTable && !hasSkippedCodesHint) pickTable(tablesFromCodes[0]);
  }, [tablesFromCodes]);

  const addMutation = useMutation({
    mutationFn: () => API.post(`/table/${selectedTable._id}/exam`, { value: act.text, type: selectedType, source }),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message || "Une erreur est survenue");
      toast.success(`« ${act.text} » ajouté à ${getTableLabel(selectedTable)}`);
      queryClient.invalidateQueries({ queryKey: ["tables"] });
      onAdded(act._id, selectedTable, selectedType);
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const ignoreMutation = useMutation({
    mutationFn: () => API.post("/ignored-term", { value: act.text }),
    onSuccess: () => {
      toast.success(`« ${act.text} » sera ignoré sur les prochaines ordonnances`);
      queryClient.invalidateQueries({ queryKey: ["ignored-terms"] });
      onIgnored(act._id);
    },
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Une erreur est survenue");
    },
  });

  const createTableIndex = results.length;
  const ignoreTermIndex = onIgnored ? results.length + 1 : null;
  const openCreateTable = () => window.open(`/table/add?exams=${encodeURIComponent(act.text || "")}`, "_blank");

  const onSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((index) => Math.min(index + 1, ignoreTermIndex ?? createTableIndex));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (results[highlighted]) pickTable(results[highlighted]);
      else if (highlighted !== ignoreTermIndex) openCreateTable();
      else if (!ignoreMutation.isPending) ignoreMutation.mutate();
    }
  };

  return (
    <FloatingPortal>
      <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 60 }} className="w-[380px] rounded-lg border border-gray-200 bg-white shadow-lg" {...getFloatingProps()}>
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3">
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Ajouter au dictionnaire</p>
            <p className="truncate text-sm font-medium text-gray-900">« {act.text} »</p>
          </div>
          <button type="button" className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600" onClick={onClose} title="Fermer">
            <HiOutlineX className="h-4 w-4" />
          </button>
        </div>

        {selectedTable ? (
          <div className="space-y-3 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] text-gray-400">Table</p>
                <p className="truncate text-sm font-medium text-gray-900">{getTableLabel(selectedTable)}</p>
                {tablesFromCodes.includes(selectedTable) && <p className="text-xs text-gray-400">Trouvée via les codes saisis par le secrétariat</p>}
              </div>
              <button
                type="button"
                className="shrink-0 text-xs text-gray-500 transition-colors hover:text-primary-700"
                onClick={() => {
                  setHasSkippedCodesHint(true);
                  setSelectedTable(null);
                }}
              >
                Changer
              </button>
            </div>
            <div>
              <p className="mb-1.5 text-[11px] text-gray-400">Catégorie</p>
              <div className="flex flex-wrap gap-1.5">
                {getTableTypes(selectedTable).map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={`rounded-md border px-2 py-0.5 text-xs font-medium transition-colors ${
                      type === selectedType ? "border-primary-500 bg-primary-50 text-primary-700" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                    onClick={() => setSelectedType(type)}
                  >
                    {EXAM_TYPE_LABEL[type]}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" className="rounded-lg px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-100" onClick={onClose}>
                Annuler
              </button>
              <button
                type="button"
                className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-600 disabled:opacity-50"
                onClick={() => addMutation.mutate()}
                disabled={addMutation.isPending || !selectedType}
              >
                {addMutation.isPending ? "Ajout..." : "Ajouter"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2">
              {isLoading ? (
                <div className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-gray-200 border-t-primary-500" />
              ) : (
                <HiOutlineSearch className="h-4 w-4 shrink-0 text-gray-400" />
              )}
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlighted(0);
                }}
                onKeyDown={onSearchKeyDown}
                placeholder="Rechercher une table (code ou libellé)..."
                className="min-w-0 flex-1 bg-transparent py-1 text-sm outline-none placeholder:text-gray-400"
              />
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {results.map((table, index) => (
                <button
                  key={table._id}
                  type="button"
                  data-active={index === highlighted || undefined}
                  className="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-gray-50 data-[active=true]:bg-primary-50/50"
                  onMouseMove={() => setHighlighted(index)}
                  onClick={() => pickTable(table)}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">{getTableLabel(table)}</p>
                    <p className="truncate text-xs text-gray-400">{tablesFromCodes.includes(table) ? "Codes saisis par le secrétariat" : getMatchingExam(table, query)}</p>
                  </div>
                  <span className="shrink-0 text-[11px] text-gray-400">
                    {getTableTypes(table)
                      .map((type) => EXAM_TYPE_LABEL[type])
                      .join(" · ")}
                  </span>
                </button>
              ))}
              {!isLoading && (
                <button
                  type="button"
                  data-active={highlighted === createTableIndex || undefined}
                  className={`flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-gray-50 data-[active=true]:bg-primary-50/50 ${results.length > 0 ? "mt-1 border-t border-gray-100 pt-3" : ""}`}
                  onMouseMove={() => setHighlighted(createTableIndex)}
                  onClick={openCreateTable}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-dashed border-gray-300 text-gray-400">
                    <HiOutlinePlus className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">Créer une nouvelle table</p>
                    <p className="truncate text-xs text-gray-400">
                      {results.length === 0 ? `Aucune table ne correspond · « ${act.text} » sera pré-rempli` : "Ouvre l'éditeur de table avec ce libellé"}
                    </p>
                  </div>
                  <HiOutlineExternalLink className="h-3.5 w-3.5 shrink-0 text-gray-300" />
                </button>
              )}
              {!isLoading && onIgnored && (
                <button
                  type="button"
                  data-active={highlighted === ignoreTermIndex || undefined}
                  className="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-gray-50 disabled:opacity-50 data-[active=true]:bg-primary-50/50"
                  onMouseMove={() => setHighlighted(ignoreTermIndex)}
                  onClick={() => ignoreMutation.mutate()}
                  disabled={ignoreMutation.isPending}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-dashed border-gray-300 text-gray-400">
                    <HiOutlineBan className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">Ignorer ce terme</p>
                    <p className="truncate text-xs text-gray-400">Ni code ni alerte sur les prochaines ordonnances</p>
                  </div>
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </FloatingPortal>
  );
};

export default AddExamPopover;
